// Automation.js - Data model for automation management
// Note: Template functions have been moved to useTemplates hook
// Static template methods below are deprecated - use useTemplates hook instead

export class Automation {
  constructor(data = {}) {
    this.id = data.id || this.generateId('automation');
    this.name = data.name || '';
    this.description = data.description || '';
    this.triggerType = data.triggerType || 'before_appointment'; // before_appointment, after_appointment, appointment_created
    this.timing = data.timing || 48; // hours before/after appointment
    this.isActive = data.isActive !== undefined ? data.isActive : true;
    this.templateId = data.templateId || '';
    this.serviceIds = data.serviceIds || [];
    this.created_at = data.created_at || new Date().toISOString();
    this.updated_at = data.updated_at || new Date().toISOString();
  }

  generateId() {
    return 'automation_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
  }

  // DEPRECATED: Use useTemplates hook instead
  // These static methods are kept for backward compatibility but should not be used
  static getTemplatePresets() {
    console.warn('Automation.getTemplatePresets() is deprecated. Use useTemplates hook instead.');
    return [];
  }

  // DEPRECATED: Use useTemplates hook instead
  static getAvailableTemplates(triggerType = null) {
    console.warn('Automation.getAvailableTemplates() is deprecated. Use useTemplates hook instead.');
    return [];
  }

  // DEPRECATED: Use useTemplates hook instead
  static getDefaultTemplateForTrigger(triggerType) {
    console.warn('Automation.getDefaultTemplateForTrigger() is deprecated. Use useTemplates hook instead.');
    return null;
  }

  // Update automation data
  update(newData) {
    Object.keys(newData).forEach(key => {
      if (key !== 'id' && key !== 'created_at') {
        this[key] = newData[key];
      }
    });
    this.updated_at = new Date().toISOString();
    return this;
  }

  // Toggle active status
  toggle() {
    this.isActive = !this.isActive;
    this.updated_at = new Date().toISOString();
    return this;
  }

  // Get trigger display text
  getTriggerDisplayText() {
    const triggerTexts = {
      before_appointment: `${this.timing} hours before appointment`,
      after_appointment: `${this.timing} hours after appointment`,
      appointment_created: 'When appointment is created'
    };
    return triggerTexts[this.triggerType] || 'Unknown trigger';
  }

  // Get human-readable services list for display
  getApplicableServices(allServices = []) {
    if (!this.serviceIds || this.serviceIds.length === 0) {
      return 'מופעל על כל השירותים'; // Applies to all services
    }

    const serviceNames = allServices
      .filter(s => this.serviceIds.includes(s.id))
      .map(s => s.name);

    return serviceNames.length > 0 ? serviceNames.join(', ') : 'Unknown Services';
  }

  // Check if automation applies to specific service
  appliesToService(serviceId) {
    if (!this.serviceIds || this.serviceIds.length === 0) {
      return true; // Applies to all services
    }
    return this.serviceIds.includes(serviceId);
  }

  // Validation methods
  static validate(data) {
    const errors = [];

    if (!data.name || data.name.trim().length === 0) {
      errors.push('Automation name is required');
    }

    if (data.name && data.name.trim().length > 100) {
      errors.push('Automation name must be less than 100 characters');
    }

    if (!data.triggerType || !this.isValidTriggerType(data.triggerType)) {
      errors.push('Valid trigger type is required');
    }

    // Only validate timing for trigger types that require it
    if (data.triggerType !== 'appointment_created' && data.triggerType !== 'appointment_approved') {
      if (!data.timing || isNaN(data.timing) || data.timing < 1 || data.timing > 168) {
        errors.push('Timing must be between 1 and 168 hours');
      }
    }

    if (!data.templateId || data.templateId.trim().length === 0) {
      errors.push('Template ID is required');
    }

    if (data.serviceIds && !Array.isArray(data.serviceIds)) {
      errors.push('Service IDs must be an array');
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  static isValidTriggerType(triggerType) {
    const validTypes = ['before_appointment', 'after_appointment', 'appointment_created', 'appointment_approved'];
    return validTypes.includes(triggerType);
  }

  // Get available trigger types with display names
  static getTriggerTypes() {
    return [
      { value: 'before_appointment', label: 'Before an appointment (Reminder)' },
      { value: 'after_appointment', label: 'After an appointment (Follow-up)' },
      { value: 'appointment_created', label: 'When an appointment is created' },
      { value: 'appointment_approved', label: 'When an appointment is approved' }
    ];
  }

  // Convert to plain object for storage
  toObject() {
    return {
      id: this.id,
      name: this.name,
      description: this.description,
      triggerType: this.triggerType,
      timing: this.timing,
      messageTemplate: this.messageTemplate,
      isActive: this.isActive,
      placeholders: this.placeholders,
      created_at: this.created_at,
      updated_at: this.updated_at
    };
  }

  // Create from stored object
  static fromObject(obj) {
    return new Automation(obj);
  }

  // Format date for display
  formatDate(date) {
    const d = new Date(date);
    return d.toLocaleDateString('he-IL', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  }

  // Format time for display  
  formatTime(time) {
    const [hours, minutes] = time.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('he-IL', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: false
    });
  }
}